/**
 * QuickBooks File Upload Component
 *
 * Handles Excel file upload, parsing, preview, and upload to QuickBooks.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Card,
  CardContent,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Send as SendIcon,
  GetApp as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  useParseFileMutation,
  useGetConnectionStatusQuery,
  useCheckDuplicatesMutation,
} from '../../../api/quickbooksApi';
import { useAppDispatch } from '../../../store/hooks';
import { showSuccessNotification } from '../../notifications';
import type { ParsedTransactions } from '../../../types/quickbooks';
import TransactionPreview from './TransactionPreview';
import { API_BASE_URL, API_VERSION } from '../../../config/api';
import { useQuickBooksUpload } from '../../../hooks/useQuickBooksUpload';

interface FileUploadProps {
  studioId: string;
  onParsedData: (data: ParsedTransactions | null) => void;
  onUploadComplete: (results: any) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  studioId,
  onParsedData,
  onUploadComplete,
}) => {
  const dispatch = useAppDispatch();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedTransactions | null>(null);
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState<string[]>(
    []
  );
  const [duplicateResults, setDuplicateResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Queries and mutations
  const { data: connectionStatus } = useGetConnectionStatusQuery(studioId);
  const [parseFile, { isLoading: isParsing }] = useParseFileMutation();
  const [checkDuplicates] = useCheckDuplicatesMutation();

  // Use async upload hook
  const {
    uploadReceipts,
    isUploading,
    progress,
    estimatedTimeRemaining,
    showTimeoutWarning,
    reset,
  } = useQuickBooksUpload();

  const isConnected = connectionStatus?.is_connected || false;

  // Handle file selected (from input or drag-drop)
  const handleFileSelected = useCallback(
    async (file: File) => {
      setError(null);

      // Validate file type
      const validExtensions = ['.xls', '.xlsx'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
      if (!validExtensions.includes(fileExtension.toLowerCase())) {
        setError(
          'Invalid file type. Please upload an Excel file (.xls or .xlsx)'
        );
        return;
      }

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setError('File is too large. Maximum size is 10MB.');
        return;
      }

      setSelectedFile(file);

      // Auto-parse the file
      try {
        const result = await parseFile({ studioId, file }).unwrap();
        setParsedData(result);
        setSelectedPaymentTypes(result.payment_types); // Select all by default
        onParsedData(result);

        // Check for duplicates (Quick Win feature)
        try {
          const dupeCheck = await checkDuplicates({
            studioId,
            grouped_transactions: result.grouped_transactions,
          }).unwrap();
          setDuplicateResults(dupeCheck);
        } catch (dupeErr) {
          // Non-critical error - log but don't block upload
          console.warn('Failed to check duplicates:', dupeErr);
        }
      } catch (err: any) {
        setError(
          err?.data?.error ||
            'Failed to parse file. Please check the file format.'
        );
        setParsedData(null);
        onParsedData(null);
      }
    },
    [studioId, parseFile, checkDuplicates, onParsedData]
  );

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelected(file);
    }
  };

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelected(file);
      }
    },
    [handleFileSelected]
  );

  // Handle payment type selection
  const handlePaymentTypeToggle = (paymentType: string) => {
    setSelectedPaymentTypes(prev =>
      prev.includes(paymentType)
        ? prev.filter(type => type !== paymentType)
        : [...prev, paymentType]
    );
  };

  // Handle upload to QuickBooks (Async)
  const handleUpload = async () => {
    if (!parsedData || !selectedFile || selectedPaymentTypes.length === 0) {
      return;
    }

    setError(null);

    // Start async upload
    await uploadReceipts({
      studioId,
      payment_types: selectedPaymentTypes,
      grouped_transactions: parsedData.grouped_transactions,
      file_name: selectedFile.name,
    });
  };

  // Handle upload completion
  React.useEffect(() => {
    if (progress?.status === 'completed' && progress.result) {
      dispatch(
        showSuccessNotification({
          message: `Successfully uploaded ${progress.result.successful_count} receipts to QuickBooks!`,
          successType: 'general',
        })
      );

      onUploadComplete(progress.result);

      // Reset form
      setSelectedFile(null);
      setParsedData(null);
      setSelectedPaymentTypes([]);
      onParsedData(null);
      reset();
    } else if (progress?.status === 'failed' && progress.error) {
      setError(
        progress.message || 'Failed to upload receipts. Please try again.'
      );
    }
  }, [progress, dispatch, onUploadComplete, onParsedData, reset]);

  if (!isConnected) {
    return (
      <Alert severity="warning">
        Please connect your QuickBooks account before uploading sales receipts.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Upload Progress - Show if upload is active (even without parsed data for resumed uploads) */}
      {isUploading && progress && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
          <Box sx={{ mb: 2 }}>
            {progress.status === 'completed' && (
              <Typography variant="h6" gutterBottom>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SuccessIcon color="success" />
                  Upload Complete
                </Box>
              </Typography>
            )}
            {progress.status === 'failed' && (
              <Typography variant="h6" gutterBottom>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon color="error" />
                  Upload Failed
                </Box>
              </Typography>
            )}
            {progress.status !== 'completed' &&
              progress.status !== 'failed' && (
                <Typography variant="h6" gutterBottom>
                  Uploading to QuickBooks...
                </Typography>
              )}

            {/* Timeout Warning */}
            {showTimeoutWarning && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This upload is taking longer than expected (48+ minutes). It
                will continue processing for up to 60 minutes. You can safely
                navigate away - the upload will continue in the background and
                results will be saved to Upload History.
              </Alert>
            )}

            <LinearProgress
              variant="determinate"
              value={progress.progress}
              sx={{ height: 8, borderRadius: 4, mb: 2 }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {progress.message}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {progress.progress.toFixed(1)}%
              </Typography>
            </Box>

            {progress.processed !== undefined &&
              progress.total !== undefined && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {progress.processed} of {progress.total} receipts processed
                  </Typography>

                  {/* Estimated Time Remaining */}
                  {estimatedTimeRemaining !== null &&
                    estimatedTimeRemaining > 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Estimated time remaining:{' '}
                        {estimatedTimeRemaining < 60
                          ? `${Math.ceil(estimatedTimeRemaining)} seconds`
                          : `${Math.ceil(estimatedTimeRemaining / 60)} minutes`}
                      </Typography>
                    )}
                </Box>
              )}

            {/* Background Upload Notice */}
            <Alert severity="info" sx={{ mt: 2 }}>
              You can safely navigate away from this page. The upload will
              continue in the background and results will be available in Upload
              History when complete.
            </Alert>
          </Box>
        </Paper>
      )}

      {/* File Upload Section */}
      {!parsedData && !isUploading && (
        <Paper
          sx={{
            p: 4,
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'divider',
            backgroundColor: dragActive ? 'action.hover' : 'background.paper',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".xls,.xlsx"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="file-upload-input"
            disabled={isParsing}
          />
          <label htmlFor="file-upload-input" style={{ cursor: 'pointer' }}>
            <UploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isParsing ? 'Parsing file...' : 'Upload Sales Receipt File'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Drag and drop your Excel file here, or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Supported formats: .xls, .xlsx (Max 10MB)
            </Typography>
          </label>

          {isParsing && (
            <Box sx={{ mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Paper>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Download Template Link */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Button
          variant="text"
          startIcon={<DownloadIcon />}
          size="small"
          href={`${API_BASE_URL}/api/${API_VERSION}/quickbooks/template/`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download Excel Template
        </Button>
      </Box>

      {/* Parsed Data Preview */}
      {parsedData && selectedFile && (
        <Box sx={{ mt: 3 }}>
          {/* Summary Cards */}
          <Typography variant="h6" gutterBottom>
            File: {selectedFile.name}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Payment Type Selection Cards */}
            {parsedData.payment_types.map(paymentType => {
              const summary = parsedData.summary[paymentType];
              const isSelected = selectedPaymentTypes.includes(paymentType);

              return (
                <Card
                  key={paymentType}
                  sx={{
                    cursor: 'pointer',
                    border: 2,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    backgroundColor: isSelected
                      ? 'action.selected'
                      : 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardContent
                    onClick={() => handlePaymentTypeToggle(paymentType)}
                  >
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isSelected}
                            onClick={e => {
                              e.stopPropagation();
                              handlePaymentTypeToggle(paymentType);
                            }}
                          />
                        }
                        label={
                          <Typography variant="h6">{paymentType}</Typography>
                        }
                      />
                    </FormGroup>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {summary.count} transaction
                      {summary.count !== 1 ? 's' : ''}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      ${summary.total.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {/* Transaction Preview */}
          <TransactionPreview
            parsedData={parsedData}
            selectedPaymentTypes={selectedPaymentTypes}
            duplicateResults={duplicateResults}
          />

          {/* Action Buttons */}
          <Box
            sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedFile(null);
                setParsedData(null);
                setSelectedPaymentTypes([]);
                onParsedData(null);
              }}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                isUploading ? <CircularProgress size={20} /> : <SendIcon />
              }
              onClick={handleUpload}
              disabled={isUploading || selectedPaymentTypes.length === 0}
            >
              {isUploading
                ? 'Uploading...'
                : `Upload ${selectedPaymentTypes.length} Payment Type${selectedPaymentTypes.length !== 1 ? 's' : ''}`}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;
